import { toast } from 'react-toastify';

/**
 * MediaKeyHandler
 * Handles media key events (Play/Pause) by setting up a silent audio player
 * and MediaSession API, similar to how YouTube captures media keys.
 * 
 * This allows the app to intercept Play/Pause keys from devices like Meta Ray-Ban glasses.
 */
class MediaKeyHandler {
  constructor() {
    this.silentAudio = null;
    this.isActive = false;
    this.onPlayPauseCallback = null;
    
    // Bind methods
    this.handleKeyboardShortcuts = this.handleKeyboardShortcuts.bind(this);
  }

  /**
   * Start listening for media keys
   * @param {Function} callback - Function to call when play/pause is pressed
   */
  start(callback) {
    if (this.isActive) {
      console.warn('MediaKeyHandler is already active');
      return;
    }

    this.onPlayPauseCallback = callback;
    this.isActive = true;

    // Set up silent audio and MediaSession API to capture media keys
    this.setupMediaSessionForKeyCapture();

    // Add keyboard shortcut listeners
    document.addEventListener('keydown', this.handleKeyboardShortcuts, true);
  }

  /**
   * Stop listening for media keys and clean up resources
   */
  stop() {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    this.onPlayPauseCallback = null;

    // Remove keyboard shortcut listener
    document.removeEventListener('keydown', this.handleKeyboardShortcuts, true);

    // Stop and clean up silent audio
    if (this.silentAudio) {
      this.silentAudio.pause();
      this.silentAudio.src = '';
      this.silentAudio = null;
    }

    // Clean up MediaSession handlers
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null;
    }

    console.log('Media key handler stopped');
  }

  /**
   * Set up MediaSession API with silent audio to make the browser treat us as an active media player.
   * This is the trick YouTube uses to capture media keys.
   */
  setupMediaSessionForKeyCapture() {
    // Create a silent audio element
    if (!this.silentAudio) {
      this.silentAudio = document.createElement('audio');
      this.silentAudio.loop = true;
      this.silentAudio.volume = 0;

      // Create a silent audio source (minimal WAV file with 1 second of silence)
      const silentAudioData =
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      this.silentAudio.src = silentAudioData;

      // Start playing the silent audio
      this.silentAudio.play().catch((err) => {
        console.warn('Could not play silent audio for media key capture:', err);
      });
    }

    // Set up MediaSession API - browser will now route media keys to us
    if ('mediaSession' in navigator) {
      // Set metadata so the OS knows we're an active media app
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Comprehend Voice Assistant',
        artist: 'ComprehendPT',
        album: 'Voice Control',
      });

      // Set the playback state to "playing"
      navigator.mediaSession.playbackState = 'playing';

      // Register action handlers for play/pause
      navigator.mediaSession.setActionHandler('play', () => {
        console.log('MediaSession PLAY action triggered');
        this.triggerCallback('play');
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        console.log('MediaSession PAUSE action triggered');
        this.triggerCallback('pause');
      });

      console.log('✅ MediaSession configured - media keys should now work!');
    }
  }

  /**
   * Handle keyboard shortcuts as alternative triggers
   */
  handleKeyboardShortcuts(event) {
    // Only handle keydown to avoid double-firing
    if (event.type !== 'keydown') {
      return;
    }

    // Option 1: Cmd+K (Mac) or Ctrl+K (Windows/Linux)
    if (
      event.key === 'k' &&
      (event.metaKey || event.ctrlKey) &&
      !event.shiftKey &&
      !event.altKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      console.log('Cmd/Ctrl+K detected');
      this.triggerCallback('keyboard-shortcut');
      return false;
    }

    // Option 2: Space bar when no input is focused
    if (event.code === 'Space') {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable);

      if (!isInputFocused) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        console.log('Space bar detected');
        this.triggerCallback('space');
        return false;
      }
    }
  }

  /**
   * Trigger the callback with the event source
   * @param {string} source - The source of the trigger ('play', 'pause', 'keyboard-shortcut', 'space')
   */
  triggerCallback(source) {
    if (this.onPlayPauseCallback && this.isActive) {
      this.onPlayPauseCallback(source);
    }
  }
}

export default MediaKeyHandler;

